import { Fragment } from 'react'

import UserIcon from '@heroicons/react/outline/esm/UserIcon.js'
import { Link } from 'react-router-dom'

import { Image, RoleType } from '@tribeplatform/gql-client/types'
import {
  SystemSpaceSlug,
  useAuthToken,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { NavbarControlAction } from '../constants.js'
import type { NavbarControlItem } from '../constants.js'
import { getNavbarControlPresentation } from '../utils.js'
import { NavbarControlCreateItem } from './NavbarControlCreateItem.js'
import { NavbarControlMessaging } from './NavbarControlMessaging.js'
import { NavbarControlNotification } from './NavbarControlNotification.js'
import { NavbarControlProfile } from './NavbarControlProfile.js'

export const NavbarControl = ({
  controlItems,
}: {
  controlItems: NavbarControlItem[]
}) => {
  const {
    data: { member, network },
  } = useAuthToken()
  const { privateMessagingEnabled } = network?.settings?.privateMessaging || {}
  const { generatePath, hasViewPermission } = useSpaceUtil()

  const signupViewPermission = hasViewPermission(SystemSpaceSlug.SIGNUP)
  const avatarUrl = (member?.profilePicture as Image)?.urls?.thumb
  const avatarName = member?.name

  const getPresentation = ({ item, props = {} }) =>
    getNavbarControlPresentation({
      item,
      avatarUrl,
      avatarName,
      props,
    })

  return (
    <div className="flex items-center space-s-3 flex-shrink-0">
      {controlItems
        .map(item => {
          switch (item.action) {
            case NavbarControlAction.CREATE_ITEM:
              return (
                <NavbarControlCreateItem
                  key={item.label}
                  item={item}
                  presentation={getPresentation({
                    item,
                    props: {
                      variant: 'primary',
                      rounded: true,
                      className: 'w-10 h-10 px-0 justify-center',
                      as: 'span',
                    },
                  })}
                />
              )
            case NavbarControlAction.SIGNUP:
              if (!signupViewPermission) {
                return null
              }
              return getPresentation({
                item,
                props: {
                  variant: 'primary',
                  className: 'hidden lg:flex',
                  as: Link,
                  to: generatePath(SystemSpaceSlug.SIGNUP),
                },
              })
            case NavbarControlAction.LOGIN:
              if (!hasViewPermission(SystemSpaceSlug.LOGIN)) {
                return null
              }
              return (
                <Fragment key={item.label}>
                  <Button
                    as={Link}
                    variant="outline"
                    rounded
                    className="w-10 h-10 px-0 justify-center lg:hidden"
                    to={generatePath(SystemSpaceSlug.LOGIN)}
                    leadingIcon={<UserIcon />}
                    aria-label={item.label}
                  />

                  {getPresentation({
                    item,
                    props: {
                      variant: 'outline',
                      className: 'hidden lg:flex',
                      as: Link,
                      to: generatePath(SystemSpaceSlug.LOGIN),
                    },
                  })}
                </Fragment>
              )
            case NavbarControlAction.SHOW_NOTIFICATION:
              if (!hasViewPermission(SystemSpaceSlug.NOTIFICATIONS)) {
                return null
              }
              return <NavbarControlNotification key={item.label} item={item} />
            case NavbarControlAction.SHOW_MESSAGING:
              if (
                !privateMessagingEnabled ||
                !member?.role ||
                member?.role?.type === RoleType.guest
              ) {
                return null
              }

              return <NavbarControlMessaging key={item.label} item={item} />
            case NavbarControlAction.SHOW_PROFILE:
              if (!hasViewPermission(SystemSpaceSlug.MEMBER)) {
                return null
              }
              return (
                <NavbarControlProfile
                  key={item.label}
                  item={item}
                  presentation={getPresentation({ item })}
                />
              )
            default:
              return null
          }
        })
        .filter(item => item)}
    </div>
  )
}
